import { PolicyUploadListResponse, PolicyComparisonListResponse, PolicyAnalysisDTO } from '../infrastructure/models';
import ApiService from "./ApiService";
import api from "./api";
import appConfig from '../AppConfig';

const ALLOWED_DOWNLOAD_ORIGINS = new Set([
    new URL(appConfig.apiBaseUrl).origin,
    // Add Azure Blob Storage origin if download URLs point there, e.g.:
    // 'https://yourstorageaccount.blob.core.windows.net',
]);

function assertAllowedDownloadUrl(url: string): void {
    try {
        const parsed = new URL(url, appConfig.apiBaseUrl);
        if (!ALLOWED_DOWNLOAD_ORIGINS.has(parsed.origin)) {
            throw new Error(`Download blocked: origin "${parsed.origin}" is not on the allowlist.`);
        }
    } catch (e) {
        if (e instanceof TypeError) throw new Error(`Download blocked: invalid URL "${url}"`);
        throw e;
    }
}

export const fetchUploadList = async (tenantId: string, offset = 0, limit = 10, signal?: AbortSignal): Promise<PolicyUploadListResponse> => {
    const response = await ApiService.get<PolicyUploadListResponse>({ url: `tenants/${tenantId}/PolicyConfigurations?offset=${offset}&limit=${limit}`, signal });
    return response;
}

export const deleteUpload = async (tenantId: string, policyExtractionId: string, signal?: AbortSignal): Promise<void> => {
    await ApiService.delete({ url: `tenants/${tenantId}/PolicyConfigurations/${policyExtractionId}`, signal });
}

export const analyzePolicy = async (tenantId: string, payload: { configurationType: number; configurationSubtype: string; continent: string; country: string; state: string }, signal?: AbortSignal): Promise<void> => {
    await ApiService.post({ url: `tenants/${tenantId}/PolicyConfigurations/analyze`, data: payload, signal });
}

export const fetchPolicyAnalysis = async (tenantId: string, offset = 0, limit = 10, signal?: AbortSignal): Promise<PolicyComparisonListResponse> => {
    const response = await ApiService.get<PolicyComparisonListResponse>({ url: `tenants/${tenantId}/PolicyConfigurations/comparisons?offset=${offset}&limit=${limit}`, signal });
    return response;
}

export const uploadNewPolicies = async (tenantId: string, configurationType: number, files: File[], signal?: AbortSignal): Promise<void> => {
    const formData = new FormData();
    files.map(file => formData.append('files', file));
    formData.append('configurationType', configurationType.toString());
    const url = `tenants/${tenantId}/PolicyConfigurations`;

    await api.post(url, formData, {
        signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
    });
}

export const fetchAnalysisDetails = async (tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<PolicyAnalysisDTO> => {
    const response = await ApiService.get<PolicyAnalysisDTO>({ url: `tenants/${tenantId}/PolicyConfigurations/comparisons/${comparisonId}`, signal });
    return response;
}

export async function downloadPolicyAnalysis(downloadUrl: string, signal?: AbortSignal): Promise<Blob> {
  assertAllowedDownloadUrl(downloadUrl);
  const response = await api.get<Blob>(downloadUrl, {
    responseType: 'blob',
    signal,
  });
  return response.data;
}